import type {FC} from 'react';
import {useCallback, useState, useRef} from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import {Utils} from '@wellstone-solutions/common';
import {TextField, Icon, IconButton, IconNames} from '@wellstone-solutions/web';

export interface SearchInputProps {
  onChange: (value: string) => void;
  label: string;
  debounceMs?: number;
  minLength?: number;
}

export const SearchInput: FC<SearchInputProps> = ({
  onChange,
  label,
  debounceMs = 300,
  minLength = 3,
}) => {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedOnChange = useCallback(
    Utils.debouncePromise(onChange, debounceMs),
    [onChange, debounceMs],
  );

  const handleChange = (newValue: string) => {
    setValue(newValue);
    if (newValue.length >= minLength || newValue.length === 0) {
      debouncedOnChange(newValue);
    }
  };

  const handleClear = () => {
    setValue('');
    onChange('');
    searchInputRef.current?.focus();
  };

  const showPlaceholder = !isFocused && value.length === 0;

  return (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      value={value}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onChange={(e) => handleChange(e.target.value)}
      InputLabelProps={
        showPlaceholder ? {shrink: false, sx: {paddingLeft: '28px'}} : {}
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Icon name={IconNames.Search} />
          </InputAdornment>
        ),
        endAdornment: value.length > 0 && (
          <InputAdornment position="end">
            <IconButton
              aria-label="Clear search"
              icon={IconNames.Xmark}
              onClick={handleClear}
            />
          </InputAdornment>
        ),
      }}
      inputRef={searchInputRef}
    />
  );
};
